import React, {memo, useCallback, useContext} from 'react'
import {BuilderContext} from '@builder.io/sdk-react'
import {useFetchEntries} from '@/hooks/useFetchEntries'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {cn} from '@/lib/utils'
import {useLocation} from 'react-router-dom'
import modelMap from '@/builder/model-map'
import {BANNER_TYPES} from '../../builder/blocks/plp/types'
import {TRANSPAENT_IMAGE} from '../../builder/blocks/plp/constants'
import {CategoryEnrichmentFetchOptions, ICategoryEnrichmentEntry} from 'hec/types/common'
import {
    useSubcategories,
    mergeWithBuilderEnrichment,
    applyFilter,
    determineLayoutClasses
} from './utils'
import {
    useCioFacetSelector,
    SelectedCioFacets,
    CioFacetState,
    SET_CIO_SELECTED_FACETS
} from '@/context/cio-facet'

export const CategoryFilters = memo(
    ({
        attributes,
        className,
        filterType,
        list,
        links,
        facets,
        layoutType
    }: CategoryEnrichmentFetchOptions) => {
        const builderContext = useContext(BuilderContext)
        const location = useLocation()
        const searchParams = new URLSearchParams(location.search)
        const apiKey = getConfig().app.builder.api

        const allFacets = useCioFacetSelector(
            (ctx) => ctx.state.cioFacets
        ) as CioFacetState['cioFacets']
        const selectedCioFacets = useCioFacetSelector(
            (ctx) => ctx.state.selectedCioFacets
        ) as SelectedCioFacets
        const dispatch = useCioFacetSelector((ctx) => ctx.dispatch)

        const toggleFacetOption = useCallback(
            (facetName: string, option: {value: string; display_name: string}) => {
                const current = selectedCioFacets[facetName] || []
                const isSelected = current.some((v) => v.value === option.value)
                dispatch({
                    type: SET_CIO_SELECTED_FACETS,
                    payload: {
                        ...selectedCioFacets,
                        [facetName]: isSelected
                            ? current.filter((v) => v.value !== option.value)
                            : [...current, option]
                    }
                })
            },
            [selectedCioFacets, dispatch]
        )

        // Static query key — filterType does not affect what is fetched, only how it renders. Cache is shared across all filterType values intentionally.
        const {
            data: categoryEnrichmentData,
            isLoading: isCategoryEnrichmentLoading,
            error: categoryEnrichmentError
        } = useFetchEntries({
            queryKey: ['category-enrichment'],
            options: {model: modelMap['categoryEnrichment'], apiKey: apiKey},
            enabled: true,
            limit: undefined,
            offset: undefined
        })

        const categoryId = builderContext.content?.data?.productCategory?.options?.category
        const sfccSubcategories = useSubcategories(categoryId)

        const rankedCategoryEnrichmentData = mergeWithBuilderEnrichment(
            sfccSubcategories,
            Array.isArray(categoryEnrichmentData)
                ? (categoryEnrichmentData as ICategoryEnrichmentEntry[])
                : []
        )
        /* eslint-disable */
        //TODO: discuss how to handle loading state, maybe spinner or skely idk
        const layoutClasses =
            filterType === BANNER_TYPES.FILTERS
                ? 'grid grid-cols-2 gap-[10px] md:flex md:flex-row md:overflow-auto md:flex-nowrap md:justify-center md:items-center'
                : determineLayoutClasses(layoutType)
        return (
            <div {...attributes} className="flex align-center justify-center">
                <div
                    className={cn(
                        layoutClasses,
                        'font-medium',
                        'lg:mx-0 w-full',
                        '[--item-width:120px] [--item-height:120px]',
                        'lg:[--item-width:120px] lg:[--item-height:120px]',
                        className
                    )}
                    data-testid="category-filters"
                >
                    {isCategoryEnrichmentLoading && <div>Loading category enrichment...</div>}

                    {filterType === BANNER_TYPES.LINK_AND_IMAGE &&
                        links?.map((item, index) => (
                            <div
                                key={`category-filter-${index}`}
                                className="flex shrink-0 flex-col items-center justify-center  flex-1  w-[--item-width]  text-center "
                            >
                                <a
                                    key={`category-filter-${index}-link`}
                                    href={item.Link}
                                    aria-label={`Filter by "${item?.['Label']}"`}
                                    data-testid="category-filters-link"
                                    className="flex shrink-0 items-center justify-center w-[--item-width] h-[--item-height] text-2xl text-center text-blue-500 rounded-full flex-cal hover:underline bg-gray-02/25"
                                >
                                    <img
                                        src={item?.['Image'] ? item?.['Image'] : TRANSPAENT_IMAGE}
                                        alt={item?.['Label']}
                                        className="object-cover w-full h-full m-6 rounded-full"
                                    />
                                </a>
                                <span className={'uppercase text-indigo-04'}>
                                    {item?.['Label']}
                                </span>
                            </div>
                        ))}

                    {filterType === BANNER_TYPES.FILTERS &&
                        list?.map((item, index) => (
                            <a
                                key={`category-filter-${index}`}
                                href={`?${applyFilter(searchParams, item).toString()}`}
                                aria-label={`Filter by "${item?.['Filter Label']}"`}
                                data-testid="category-filters-link"
                                className="flex shrink-0 flex-0 items-center justify-center text-sm  text-center text-black rounded-full w-48 h-9 hover:underline bg-gray-02/25 border border-black"
                            >
                                {item?.['Filter Label']}
                            </a>
                        ))}
                    {filterType === BANNER_TYPES.FACETS &&
                        facets?.map(({name, value, display_name}) => {
                            const facetData = allFacets.find((f) => f.name === name)
                            const validOption = facetData?.options.find((o) => o.value === value)
                            if (!facetData || !validOption) return null
                            const label = display_name || validOption.display_name
                            const isSelected = (selectedCioFacets[name] || []).some(
                                (v) => v.value === value
                            )
                            return (
                                <button
                                    key={`facet-${name}-${value}`}
                                    onClick={() =>
                                        toggleFacetOption(name, {value, display_name: label})
                                    }
                                    aria-label={`Filter by "${label}"`}
                                    aria-pressed={isSelected}
                                    data-testid="category-filters-facet"
                                    className={cn(
                                        'flex shrink-0 flex-col items-center justify-center p-10 text-2xl text-center rounded-full w-[--item-width] h-[--item-height]',
                                        isSelected
                                            ? 'bg-indigo-04 text-white'
                                            : 'bg-gray-02/25 text-blue-500 hover:underline'
                                    )}
                                >
                                    {label}
                                </button>
                            )
                        })}

                    {filterType === BANNER_TYPES.CATEGORIES &&
                        rankedCategoryEnrichmentData?.map((item, index) => (
                            <div
                                key={`category-filter-${index}` + item.id}
                                className="flex shrink-0 flex-col items-center justify-center  flex-1  w-[--item-width]  text-center gap-4"
                            >
                                <a
                                    key={`category-filter-${index}-link`}
                                    href={`${location.pathname}/${item.categoryShortName}`}
                                    aria-label={`Filter by "${item.name}"`}
                                    data-testid="category-filters-link"
                                    className="flex flex-shrink-0 flex-grow-0 flex-auto items-center justify-center w-[--item-width] h-[--item-height] text-2xl text-center text-blue-500 rounded-full flex-cal hover:underline bg-gray-02/25"
                                >
                                    <img
                                        src={item.categoryIcon ?? TRANSPAENT_IMAGE}
                                        alt={item.name}
                                        className="object-cover w-full h-full m-6 rounded-full"
                                    />
                                </a>
                                <span className={'uppercase text-indigo-04 min-h-[48px]'}>
                                    {item.name}
                                </span>
                            </div>
                        ))}
                </div>
            </div>
        )
        /* eslint-enable */
    }
)
//fordevtools
CategoryFilters.displayName = 'CategoryFilters'
